/**
 * Voxly — Voice filter utilities.
 *
 * Pure functions extracted from VoiceRecorder.jsx so they can be reused
 * (currently used by VoiceRecorder; could be reused by a future audio-only
 * editor surface).
 *
 * Exports:
 *   VOICE_FILTERS — list of preset descriptors
 *   buildFilterChain(ctx, source, preset) — wires up Web Audio nodes for a preset
 *   audioBufferToWav(buffer) — encodes an AudioBuffer to a PCM-16 WAV Blob
 *   applyFilterToArrayBuffer(arrayBuffer, preset) — re-render audio with a different filter
 */

export const VOICE_FILTERS = [
  { id: "none", label: "Natural", short: "—" },
  // Quality / mastering presets — these change the SOUND, not the character.
  { id: "studio", label: "Studio", short: "🎙" },
  { id: "podcast", label: "Podcast", short: "🎧" },
  { id: "bright", label: "Bright", short: "✦" },
  { id: "warm", label: "Warm", short: "♨" },
  { id: "deep", label: "Deep", short: "🌑" },
  // Character / fun presets — these change WHO you sound like.
  { id: "telephone", label: "Phone", short: "☎" },
  { id: "radio", label: "Radio", short: "📻" },
  { id: "megaphone", label: "Megaphone", short: "📣" },
  { id: "echo", label: "Echo", short: "🔊" },
  { id: "church", label: "Cathedral", short: "⛪" },
  { id: "tunnel", label: "Tunnel", short: "🚇" },
  { id: "stadium", label: "Stadium", short: "🏟" },
  { id: "underwater", label: "Underwater", short: "🌊" },
  { id: "robot", label: "Robot", short: "🤖" },
  { id: "monster", label: "Monster", short: "👹" },
  { id: "chipmunk", label: "Chipmunk", short: "🐿" },
  { id: "alien", label: "Alien", short: "👽" },
  { id: "vintage_radio", label: "Vintage", short: "📼" },
  { id: "whisper", label: "Whisper", short: "🤫" },
];

/** How much silence the renderer must add after the source to capture wet
 *  tails (reverb/echo decay). Without this the OfflineAudioContext truncates
 *  to the source length and reverb sounds barely there. */
const PRESET_TAIL_SECS = {
  echo: 2.5,
  church: 3.5,
  tunnel: 3.0,
  stadium: 4.0,
  monster: 0.4,
  whisper: 0.2,
  vintage_radio: 0.3,
};

/** Some presets need to be played at a different rate (pitch shift) — we
 *  set this on the AudioBufferSourceNode in applyFilterToArrayBuffer below. */
const PRESET_PLAYBACK_RATE = {
  chipmunk: 1.55, // higher pitch + shorter
  monster: 0.75,  // lower pitch + longer
};

/** Build a chain of Web Audio nodes for the given preset. Returns the FINAL node
 *  in the chain (caller must connect that to ctx.destination or recorder dest). */
export function buildFilterChain(ctx, source, preset) {
  const passthrough = preset === "none";
  if (passthrough) return source;

  const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 80;
  const peakLow = ctx.createBiquadFilter(); peakLow.type = "lowshelf";
  const peakHigh = ctx.createBiquadFilter(); peakHigh.type = "highshelf";
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -22; compressor.knee.value = 14; compressor.ratio.value = 5;
  compressor.attack.value = 0.005; compressor.release.value = 0.12;
  const gain = ctx.createGain(); gain.gain.value = 1.0;

  switch (preset) {
    // === Quality / mastering ===
    case "studio":
      // Broadcast-style EQ: tight lows, presence boost, air boost, hard compress
      hp.frequency.value = 100;
      peakLow.frequency.value = 200; peakLow.gain.value = 8;
      peakHigh.frequency.value = 5000; peakHigh.gain.value = 10;
      gain.gain.value = 1.6;
      break;
    case "podcast": {
      // De-essed + tight + warm — like NPR mics
      hp.frequency.value = 120;
      const deess = ctx.createBiquadFilter(); deess.type = "peaking"; deess.frequency.value = 7000; deess.Q.value = 1.5; deess.gain.value = -8;
      peakLow.frequency.value = 180; peakLow.gain.value = 6;
      peakHigh.frequency.value = 4000; peakHigh.gain.value = 5;
      gain.gain.value = 1.5;
      source.connect(hp); hp.connect(peakLow); peakLow.connect(peakHigh); peakHigh.connect(deess); deess.connect(compressor); compressor.connect(gain);
      return gain;
    }
    case "bright":
      // Crisp / sharp / sparkly
      peakHigh.frequency.value = 4000; peakHigh.gain.value = 18;
      peakLow.frequency.value = 250; peakLow.gain.value = -6;
      gain.gain.value = 1.3;
      break;
    case "warm":
      // Smooth, mellow, vintage
      peakLow.frequency.value = 250; peakLow.gain.value = 12;
      peakHigh.frequency.value = 6500; peakHigh.gain.value = -10;
      gain.gain.value = 1.2;
      break;
    case "deep":
      // Movie-trailer voice — drop the entire signal down with massive low boost
      peakLow.frequency.value = 150; peakLow.gain.value = 18;
      peakHigh.frequency.value = 4500; peakHigh.gain.value = -8;
      gain.gain.value = 1.4;
      break;

    // === Character / fun ===
    case "telephone": {
      // 300-3400 Hz bandpass + heavy compression — vintage phone line
      hp.frequency.value = 350;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3200;
      const drive = ctx.createWaveShaper();
      const dc = new Float32Array(256);
      for (let i = 0; i < 256; i++) { const x = (i / 128) - 1; dc[i] = Math.tanh(x * 1.5); }
      drive.curve = dc;
      gain.gain.value = 1.6;
      source.connect(hp); hp.connect(lp); lp.connect(drive); drive.connect(gain);
      return gain;
    }
    case "radio": {
      // AM-radio bandpass + heavy saturation
      hp.frequency.value = 250;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 4500;
      peakLow.frequency.value = 1200; peakLow.gain.value = 10;
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(512);
      for (let i = 0; i < 512; i++) { const x = (i / 256) - 1; curve[i] = Math.tanh(x * 4); }
      shaper.curve = curve; shaper.oversample = "4x";
      gain.gain.value = 1.4;
      source.connect(hp); hp.connect(lp); lp.connect(peakLow); peakLow.connect(shaper); shaper.connect(gain);
      return gain;
    }
    case "underwater": {
      // Steep low-pass + flanging delay
      const lp1 = ctx.createBiquadFilter(); lp1.type = "lowpass"; lp1.frequency.value = 500; lp1.Q.value = 4;
      const lp2 = ctx.createBiquadFilter(); lp2.type = "lowpass"; lp2.frequency.value = 700; lp2.Q.value = 2;
      const delay = ctx.createDelay(); delay.delayTime.value = 0.08;
      const feedback = ctx.createGain(); feedback.gain.value = 0.45;
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.3; lfo.type = "sine";
      const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.02;
      lfo.connect(lfoGain); lfoGain.connect(delay.delayTime); lfo.start();
      gain.gain.value = 1.4;
      source.connect(lp1); lp1.connect(lp2); lp2.connect(delay); delay.connect(feedback); feedback.connect(delay); delay.connect(gain); lp2.connect(gain);
      return gain;
    }
    case "megaphone": {
      // Mid-only bandpass + hard tanh saturation — bullhorn timbre
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1700; bp.Q.value = 3;
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(512);
      for (let i = 0; i < 512; i++) { const x = (i / 256) - 1; curve[i] = Math.tanh(x * 10); }
      shaper.curve = curve; shaper.oversample = "4x";
      gain.gain.value = 2.0;
      source.connect(bp); bp.connect(shaper); shaper.connect(gain);
      return gain;
    }
    case "echo": {
      // Strong slapback delay — 6 audible echoes
      const delay = ctx.createDelay(2.0); delay.delayTime.value = 0.4;
      const feedback = ctx.createGain(); feedback.gain.value = 0.65;
      const wet = ctx.createGain(); wet.gain.value = 1.0;
      source.connect(delay); delay.connect(feedback); feedback.connect(delay); delay.connect(wet); wet.connect(gain);
      source.connect(gain);
      return gain;
    }
    case "robot": {
      // Ring modulator + tight low-pass — quintessential robot voice
      const osc = ctx.createOscillator(); osc.frequency.value = 70; osc.type = "sine";
      const mod = ctx.createGain(); mod.gain.value = 0;
      osc.connect(mod.gain); osc.start();
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3000;
      gain.gain.value = 1.4;
      source.connect(mod); mod.connect(lp); lp.connect(gain);
      return gain;
    }
    case "whisper": {
      hp.frequency.value = 250;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3500;
      gain.gain.value = 1.7;
      const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
      const ch = noiseBuf.getChannelData(0);
      for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * 0.04;
      const noise = ctx.createBufferSource(); noise.buffer = noiseBuf; noise.loop = true; noise.start();
      const noiseGain = ctx.createGain(); noiseGain.gain.value = 0.25;
      noise.connect(noiseGain); noiseGain.connect(gain);
      source.connect(hp); hp.connect(lp); lp.connect(gain);
      return gain;
    }
    case "church": {
      // 2.5s gated impulse response — gorgeous cathedral reverb
      const irLen = Math.floor(ctx.sampleRate * 2.5);
      const ir = ctx.createBuffer(2, irLen, ctx.sampleRate);
      for (let c = 0; c < 2; c++) {
        const chData = ir.getChannelData(c);
        for (let i = 0; i < irLen; i++) {
          const t = i / irLen;
          chData[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 1.8);
        }
      }
      const conv = ctx.createConvolver(); conv.buffer = ir;
      const wet = ctx.createGain(); wet.gain.value = 1.4;
      const dry = ctx.createGain(); dry.gain.value = 0.5;
      source.connect(dry); dry.connect(gain);
      source.connect(conv); conv.connect(wet); wet.connect(gain);
      return gain;
    }
    case "tunnel": {
      // Cavernous delay + low-pass for "I'm in a long concrete tunnel" feel
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1500;
      const delay = ctx.createDelay(1.5); delay.delayTime.value = 0.45;
      const feedback = ctx.createGain(); feedback.gain.value = 0.65;
      const wet = ctx.createGain(); wet.gain.value = 1.1;
      source.connect(lp); lp.connect(gain);
      lp.connect(delay); delay.connect(feedback); feedback.connect(delay);
      delay.connect(wet); wet.connect(gain);
      return gain;
    }
    case "stadium": {
      // Multi-tap delay for arena feel
      const delays = [0.18, 0.34, 0.55, 0.78];
      const wet = ctx.createGain(); wet.gain.value = 0.55;
      delays.forEach((dt) => {
        const d = ctx.createDelay(); d.delayTime.value = dt;
        const g = ctx.createGain(); g.gain.value = 0.6 - (dt * 0.4);
        source.connect(d); d.connect(g); g.connect(wet);
      });
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 4500;
      wet.connect(lp); lp.connect(gain);
      source.connect(gain);
      return gain;
    }
    case "monster": {
      // Heavy distortion + low-pass + bass boost — guttural growl
      // Note: monster also uses playbackRate=0.75 in applyFilterToArrayBuffer
      // so the actual voice drops in pitch as well.
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1800;
      peakLow.frequency.value = 80; peakLow.gain.value = 16;
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(512);
      for (let i = 0; i < 512; i++) { const x = (i / 256) - 1; curve[i] = Math.tanh(x * 10); }
      shaper.curve = curve; shaper.oversample = "4x";
      gain.gain.value = 1.5;
      source.connect(peakLow); peakLow.connect(lp); lp.connect(shaper); shaper.connect(gain);
      return gain;
    }
    case "chipmunk": {
      // High-pitch via playbackRate=1.55 in applyFilterToArrayBuffer + bright EQ
      peakHigh.frequency.value = 3500; peakHigh.gain.value = 6;
      gain.gain.value = 1.2;
      source.connect(peakHigh); peakHigh.connect(gain);
      return gain;
    }
    case "vintage_radio": {
      // Lo-fi 1930s telephone + crackle + saturation
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1500; bp.Q.value = 1.5;
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(256);
      for (let i = 0; i < 256; i++) { const x = (i / 128) - 1; curve[i] = Math.tanh(x * 5); }
      shaper.curve = curve; shaper.oversample = "4x";
      const crackleBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.8), ctx.sampleRate);
      const cd = crackleBuf.getChannelData(0);
      for (let i = 0; i < cd.length; i++) {
        cd[i] = Math.random() < 0.01 ? (Math.random() * 2 - 1) * 0.7 : (Math.random() * 2 - 1) * 0.06;
      }
      const noise = ctx.createBufferSource(); noise.buffer = crackleBuf; noise.loop = true; noise.start();
      const noiseGain = ctx.createGain(); noiseGain.gain.value = 0.22;
      noise.connect(noiseGain); noiseGain.connect(gain);
      gain.gain.value = 1.5;
      source.connect(bp); bp.connect(shaper); shaper.connect(gain);
      return gain;
    }
    case "alien": {
      // Ring mod at 40 Hz + LFO-modulated delay for warbling otherworldly feel
      const osc = ctx.createOscillator(); osc.frequency.value = 40; osc.type = "sine";
      const mod = ctx.createGain(); mod.gain.value = 0;
      osc.connect(mod.gain); osc.start();
      const delay = ctx.createDelay(0.1);
      const lfo = ctx.createOscillator(); lfo.frequency.value = 5; lfo.type = "sine";
      const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.012;
      lfo.connect(lfoGain); lfoGain.connect(delay.delayTime); lfo.start();
      delay.delayTime.value = 0.02;
      gain.gain.value = 1.3;
      source.connect(mod); mod.connect(delay); delay.connect(gain);
      return gain;
    }
    default: break;
  }
  // Default chain (studio / bright / warm / deep): HP → low shelf → high shelf → compressor → gain
  source.connect(hp); hp.connect(peakLow); peakLow.connect(peakHigh); peakHigh.connect(compressor); compressor.connect(gain);
  return gain;
}

/** PCM-16 WAV encoder for an AudioBuffer (44-byte RIFF header + LE PCM). */
export function audioBufferToWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const samples = buffer.length;
  const blockAlign = numCh * 2;
  const byteRate = sampleRate * blockAlign;
  const dataLen = samples * blockAlign;
  const ab = new ArrayBuffer(44 + dataLen);
  const view = new DataView(ab);
  const writeStr = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataLen, true);
  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numCh; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

/** Decode a raw audio ArrayBuffer, render through the given preset offline,
 *  return a WAV Blob. Used to swap voice filters after a single recording.
 *
 *  Key correctness detail: the OfflineAudioContext length must include any
 *  reverb/delay TAIL (`PRESET_TAIL_SECS`) — otherwise the wet decay gets
 *  truncated and reverb-heavy presets sound barely different from dry.
 *  Pitch-shift presets (chipmunk/monster) also adjust `playbackRate` here. */
export async function applyFilterToArrayBuffer(arrayBuffer, preset) {
  const decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
  // decodeAudioData mutates the buffer reference on some browsers, so clone.
  const decoded = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
  await decodeCtx.close();

  const playbackRate = PRESET_PLAYBACK_RATE[preset] || 1.0;
  const tailSecs = PRESET_TAIL_SECS[preset] || 0;
  const renderLen = Math.ceil(decoded.length / playbackRate + decoded.sampleRate * tailSecs);

  const offline = new OfflineAudioContext(decoded.numberOfChannels, renderLen, decoded.sampleRate);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.playbackRate.value = playbackRate;
  const finalNode = buildFilterChain(offline, source, preset);
  finalNode.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return audioBufferToWav(rendered);
}
